import { CloudSyncOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Badge, Button, Card, List, Pagination, Segmented, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { shipmentsApi, scopeParams } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { ListShipmentsParams, ShipmentStatus } from '../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { ActorLine } from '../components/ActorLine';
import { ShipmentStatusTag } from '../components/ShipmentStatusTag';
import { useStoreContext } from '../context/StoreContext';
import { formatKopeks } from '../lib/money';

const PAGE_SIZE = 50;

type StatusFilter = ShipmentStatus | 'ALL';

/**
 * /shipments — накладные из МойСклад (SPEC §1 integration): the pricing inbox.
 * Default filter shows what awaits prices; «Обновить из МойСклад» is the
 * safety net for missed webhooks.
 */
export function ShipmentsPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { storeId: ctxStoreId, storeIds: ctxStoreIds } = useStoreContext();
  const [status, setStatus] = useState<StatusFilter>('PENDING_PRICING');
  const [page, setPage] = useState(1);

  const params: ListShipmentsParams = {
    ...(status === 'ALL' ? {} : { status }),
    ...scopeParams({ storeId: ctxStoreId, storeIds: ctxStoreIds }),
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.shipments(params as Record<string, unknown>),
    queryFn: () => shipmentsApi.list(params),
    placeholderData: keepPreviousData,
  });

  const refreshMutation = useMutation({
    mutationFn: () => shipmentsApi.refresh(),
    onSuccess: (counts) => {
      const brought = counts.created + counts.updated + counts.deleted_pending;
      message.success(
        brought > 0
          ? t('shipments.refreshDone', { created: counts.created, updated: counts.updated })
          : t('shipments.refreshNothing'),
      );
      void queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <Segmented<StatusFilter>
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={[
            { value: 'PENDING_PRICING', label: t('shipments.filterPending') },
            { value: 'POSTED', label: t('shipments.filterPosted') },
            { value: 'ALL', label: t('shipments.filterAll') },
          ]}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            icon={<CloudSyncOutlined />}
            onClick={() => refreshMutation.mutate()}
            loading={refreshMutation.isPending}
          >
            {t('shipments.refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/shipments/new')}>
            {t('shipments.createManual')}
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyBlock description={t('shipments.empty')} hint={t('shipments.emptyHint')} />
      ) : (
        <>
          <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
            <List
              dataSource={query.data.items}
              renderItem={(row) => (
                <List.Item
                  onClick={() => navigate(`/shipments/${row.id}`)}
                  style={{ cursor: 'pointer', paddingInline: 4 }}
                  extra={<RightOutlined style={{ color: '#bbb' }} />}
                >
                  <List.Item.Meta
                    title={
                      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {t('shipments.rowTitle', { name: row.msName })}
                        <ShipmentStatusTag status={row.status} />
                        {row.source === 'MANUAL' ? <Tag>{t('shipments.manualTag')}</Tag> : null}
                        {row.msChangedAfterPost ? (
                          <Tag color="orange">{t('shipments.changedInMs')}</Tag>
                        ) : null}
                        {row.msDeletedAt ? <Tag color="red">{t('shipments.deletedInMs')}</Tag> : null}
                      </span>
                    }
                    description={
                      <>
                        <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>{dayjs(row.msDate).format('D MMMM YYYY')}</span>
                          <span>
                            {row.skladName ? `${row.skladName} → ` : ''}
                            {row.storeName}
                          </span>
                          <Badge
                            color={row.pricedCount === row.itemCount ? 'green' : 'gold'}
                            text={t('shipments.pricedOf', {
                              priced: row.pricedCount,
                              total: row.itemCount,
                            })}
                          />
                        </span>
                        <ActorLine createdBy={row.createdByName} postedBy={row.postedByName} />
                      </>
                    }
                  />
                  {/* Purchase total; null (hidden) for a store SELLER. */}
                  {row.msSum != null ? (
                    <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
                      {formatKopeks(row.msSum)}
                    </Typography.Text>
                  ) : null}
                </List.Item>
              )}
            />
          </Card>
          {query.data.total > PAGE_SIZE ? (
            <Pagination
              current={page}
              pageSize={PAGE_SIZE}
              total={query.data.total}
              onChange={setPage}
              showSizeChanger={false}
              style={{ textAlign: 'center' }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
